import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, COMPANY } from "@/data";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-black/85 backdrop-blur-2xl border-white/20 text-[#F5F4EF]"
          : "bg-transparent border-transparent text-black"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3"
        >
          <span className="h-10 w-10 bg-primary flex items-center justify-center font-mono-accent text-lg text-white">R</span>
          <span className="font-display text-xl md:text-2xl font-semibold tracking-tight leading-none">
            RICH GLOBAL <span className="text-primary">RMC</span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="font-mono-accent text-xs uppercase tracking-[0.2em] opacity-80 hover:opacity-100 hover:text-primary transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-quote-btn"
            onClick={() => go("#quote")}
            className="font-mono-accent text-xs uppercase tracking-[0.2em] bg-primary text-white px-6 py-3 hover:bg-signal-yellow hover:text-black transition-colors"
          >
            Get a Quote
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-2xl text-[#F5F4EF]"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                className="font-mono-accent text-xs uppercase tracking-[0.2em] bg-primary text-white px-6 py-4 text-center"
              >
                Call 24×7
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
